import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';

/**
 * Helper to update URL search params without triggering React re-renders.
 * Uses replaceState directly to avoid useSearchParams subscription issues.
 */
const updateUrlParams = (updates) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    window.history.replaceState({}, '', url.toString());
};

/**
 * Zustand store for Source Package Builder modal state.
 *
 * Strategy:
 * - READ from URL only once on mount (via initFromUrl)
 * - WRITE to URL on every state change (via subscribeWithSelector)
 * - Components subscribe to Zustand, not URL params
 *
 * This prevents re-renders from URL changes while keeping URL in sync for reload.
 */
const useSourcePackageBuilderStore = create(
    subscribeWithSelector((set, get) => ({
        // Modal visibility
        isOpen: false,

        // What the center pane renders: 'corpus' (segment document view), 'file' (My Files
        // data-item renderer), or 'report' (read-only report view). My Files + Reports now
        // open in this same overlay instead of a separate modal / new browser tab.
        viewKind: 'corpus',

        // Document being viewed
        documentId: null,
        segmentIndex: null,

        // Transient payloads for non-corpus views (not URL-persisted).
        filePayload: null,
        reportPayload: null,

        // The federated search hit that was opened (transient, not URL-persisted) so the
        // right-hand panel can surface the selected segment as a rich card.
        selectedHit: null,

        // Whether initial sync from URL has happened
        _initialized: false,

        // Open the builder with a specific document
        open: ({
            documentId = null,
            segmentIndex = null,
            selectedHit = null,
            viewKind = 'corpus',
            filePayload = null,
            reportPayload = null,
        } = {}) => {
            if (viewKind === 'corpus' && !documentId) {
                console.warn('[SourcePackageBuilderStore] open() called without documentId');
                return;
            }
            if (viewKind === 'file' && !filePayload?.fileId) {
                console.warn('[SourcePackageBuilderStore] open() file view called without filePayload.fileId');
                return;
            }
            if (viewKind === 'report' && !reportPayload?.reportId) {
                console.warn('[SourcePackageBuilderStore] open() report view called without reportPayload.reportId');
                return;
            }
            set({
                isOpen: true,
                viewKind,
                documentId,
                segmentIndex,
                filePayload,
                reportPayload,
                selectedHit,
            });
        },

        // Close the builder
        close: () => {
            set({
                isOpen: false,
                viewKind: 'corpus',
                documentId: null,
                segmentIndex: null,
                filePayload: null,
                reportPayload: null,
                selectedHit: null,
            });
        },

        // View a different document while builder is open
        setDocument: ({
            documentId = null,
            segmentIndex = null,
            selectedHit = null,
            viewKind = 'corpus',
            filePayload = null,
            reportPayload = null,
        }) => {
            set({documentId, segmentIndex, selectedHit, viewKind, filePayload, reportPayload});
        },

        // Initialize from URL params (called once on mount)
        initFromUrl: (searchParams) => {
            if (get()._initialized) return;

            const documentId = searchParams.get('documentId');
            const segmentIndex = searchParams.get('segmentIndex');
            const isBuilderOpen = searchParams.get('isBuilderOpen') === 'true';

            if (documentId && isBuilderOpen) {
                set({
                    isOpen: true,
                    viewKind: 'corpus',
                    documentId,
                    segmentIndex: segmentIndex ? parseInt(segmentIndex, 10) : null,
                    _initialized: true,
                });
            } else {
                set({_initialized: true});
            }
        },

        // Reset state (useful when navigating away)
        reset: () => {
            set({
                isOpen: false,
                viewKind: 'corpus',
                documentId: null,
                segmentIndex: null,
                filePayload: null,
                reportPayload: null,
                selectedHit: null,
                _initialized: false,
            });
        },
    }))
);

// Subscribe to state changes and sync to URL
useSourcePackageBuilderStore.subscribe(
    (state) => ({isOpen: state.isOpen, documentId: state.documentId, segmentIndex: state.segmentIndex}),
    ({isOpen, documentId, segmentIndex}) => {
        // Only sync after initialization
        if (!useSourcePackageBuilderStore.getState()._initialized) return;

        if (isOpen && documentId) {
            updateUrlParams({
                isBuilderOpen: 'true',
                documentId,
                segmentIndex: segmentIndex ?? null,
            });
        } else {
            updateUrlParams({
                isBuilderOpen: null,
                documentId: null,
                segmentIndex: null,
            });
        }
    },
    {equalityFn: (a, b) => a.isOpen === b.isOpen && a.documentId === b.documentId && a.segmentIndex === b.segmentIndex}
);

export default useSourcePackageBuilderStore;
